import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 皮肤的 React 绑定：切换、持久化、跟着改浏览器界面色。
 *
 * 用不用这个 Provider 是可选的——皮肤本质上就是 `<html>` 上的两个属性，
 * 应用完全可以自己写。它存在只是为了把"持久化 + theme-color + 跟随系统"这三件
 * 每个项目都要重做一遍的事收口掉。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { applySkin, ensureSkinStyles, readSkinPreference, resolveDark, skinThemeColor, syncThemeColorMeta, writeSkinPreference, } from '../skin/runtime.js';
import { builtinSkins } from '../skin/presets.js';
const SkinContext = createContext(null);
export function useSkin() {
    const ctx = useContext(SkinContext);
    if (!ctx)
        throw new Error('useSkin() 必须在 <SkinProvider> 里面用');
    return ctx;
}
export function SkinProvider({ skins = builtinSkins, defaultSkin, namespace, persist = true, injectStyles = true, children, }) {
    const fallback = defaultSkin ?? skins[0]?.name ?? 'default';
    const known = useMemo(() => skins.map((s) => s.name), [skins]);
    // 初值必须在**第一次渲染时**就读出来，不能等 effect：
    // 等 effect 的话首屏会先用默认皮肤画一帧，然后闪一下变成用户选的那套。
    // （index.html 里那段同步脚本已经把属性写好了，这里读的是同一份数据源，两边要对上。）
    const [pref, setPref] = useState(() => {
        // ⚠️ 在**第一次渲染期间**注入样式，不是在 effect 里。
        // 放 effect 里的话 React 已经用默认皮肤画过一帧，品牌色会闪一下。
        if (injectStyles)
            ensureSkinStyles(skins, fallback);
        return persist ? readSkinPreference(namespace, known, fallback) : { skin: fallback, mode: 'system' };
    });
    // 皮肤列表变了（热更新、按账号下发）要重新注入
    useEffect(() => {
        if (injectStyles)
            ensureSkinStyles(skins, fallback);
    }, [skins, fallback, injectStyles]);
    const skin = useMemo(() => skins.find((s) => s.name === pref.skin) ?? skins[0] ?? builtinSkins[0], [skins, pref.skin]);
    // 属性 + 浏览器界面色。放在 layout 之前跑没有意义（属性已由 boot script 写过），
    // 这里负责的是**之后每一次切换**。
    useEffect(() => {
        applySkin(skin.name, pref.mode);
        syncThemeColorMeta(skinThemeColor(skin, pref.mode));
    }, [skin, pref.mode]);
    // 跟随系统时，系统入夜要能立刻反映到 theme-color 上（属性不用改，CSS 的媒体查询会自己生效）
    useEffect(() => {
        if (pref.mode !== 'system' || typeof window === 'undefined' || !window.matchMedia)
            return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => syncThemeColorMeta(skinThemeColor(skin, 'system'));
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [skin, pref.mode]);
    const setSkin = useCallback((name) => {
        if (!known.includes(name))
            return;
        setPref((p) => ({ ...p, skin: name }));
        if (persist)
            writeSkinPreference(namespace, { skin: name });
    }, [known, namespace, persist]);
    const setMode = useCallback((mode) => {
        setPref((p) => ({ ...p, mode }));
        if (persist)
            writeSkinPreference(namespace, { mode });
    }, [namespace, persist]);
    const value = useMemo(() => ({ skin, skins, mode: pref.mode, isDark: resolveDark(pref.mode), setSkin, setMode }), [skin, skins, pref.mode, setSkin, setMode]);
    return _jsx(SkinContext.Provider, { value: value, children: children });
}
/**
 * 一个够用的皮肤 + 明暗切换器。设置页直接放进去就行。
 *
 * 明暗是**三个选项**而不是一个开关：「跟随系统」是绝大多数人想要的默认，
 * 做成开关就等于强迫用户在两个具体值里选一个，然后他的手机入夜时应用不跟着变。
 */
export function SkinPicker({ modeOnly = false, className = '' }) {
    const { skin, skins, mode, setSkin, setMode } = useSkin();
    return (_jsxs("div", { className: `ps-skin-picker ${className}`.trim(), children: [!modeOnly && skins.length > 1 && (_jsx("div", { className: "ps-skin-row", role: "radiogroup", "aria-label": "\u76AE\u80A4", children: skins.map((s) => (_jsxs("button", { type: "button", role: "radio", "aria-checked": s.name === skin.name, className: `ps-skin-swatch ${s.name === skin.name ? 'is-active' : ''}`.trim(), onClick: () => setSkin(s.name), title: s.label, children: [_jsx("span", { className: "ps-skin-dot", style: { background: s.light.accent, borderColor: s.light.accentLine }, "aria-hidden": "true" }), _jsx("span", { className: "ps-skin-label", children: s.label })] }, s.name))) })), _jsx("div", { className: "ps-skin-row", role: "radiogroup", "aria-label": "\u5916\u89C2", children: [
                    ['system', '跟随系统'],
                    ['light', '浅色'],
                    ['dark', '深色'],
                ].map(([value, label]) => (_jsx("button", { type: "button", role: "radio", "aria-checked": mode === value, className: `ps-skin-mode ${mode === value ? 'is-active' : ''}`.trim(), onClick: () => setMode(value), children: label }, value))) })] }));
}
//# sourceMappingURL=skin-provider.js.map